import type { Aabb } from './types'
import { claimSlot, releaseSlot, stepUnit } from './steering'
import { nextRng, rangeRng } from './rng'
import {
  boxAdjacentToTileSet,
  buildingBoxAt,
  coveredTiles,
  isTileAreaFree,
  makeBuilding,
  poolSpot,
  queueWaitPos,
  tileKey,
  tileKeySet,
  type SimState,
  type SimUnit,
} from './state'
import type {
  AssignLaborCommand,
  Command,
  PlaceBuildingCommand,
} from './commands'
import {
  accessSlotCount,
  deriveChainPullEdges,
  type Content,
  type ContentId,
  type RecipeDef,
} from '../content/schema'

// tick() is the only writer of game state. It runs nine phases in a fixed
// order; no phase writes outside its own step. Inventory moves are two-phase:
// movement (phase 4) reserves a pickup/dropoff, commit (phase 5) applies it, so
// a future contested draw can resolve claims without reshaping the pipeline.
// Phases with no behavior yet are named no-op stubs.
//
// `content` is immutable validated data, not part of SimState, so it never
// enters the determinism or save serialization.

function recipeOf(
  content: Content,
  buildingId: ContentId,
): RecipeDef | undefined {
  const building = content.buildings.get(buildingId)
  return building ? content.recipes.get(building.recipe) : undefined
}

// Phase 1: drain the command queue.
function drainCommands(state: SimState, content: Content): void {
  const queue = state.commandQueue
  state.commandQueue = []
  for (const cmd of queue) {
    state.drainedCommandCount += 1
    applyCommand(state, content, cmd)
  }
}

// Place a building of the command's content id on its target tile. Validates
// before any mutation and rejects an invalid placement as a no-op that leaves
// state fully unchanged (not a throw — unlike the duplicate-spawn guard, an
// invalid placement is player error, not an authoring bug that must kill the
// sim). Rejects when the content id is unknown, the tile is out of bounds, or
// any tile the building's footprint would cover is already occupied.
function applyPlaceBuilding(
  state: SimState,
  content: Content,
  cmd: PlaceBuildingCommand,
): void {
  const def = content.buildings.get(cmd.id)
  if (def === undefined) return
  if (!isTileAreaFree(state.grid, cmd.tile)) return
  // Accept: build from the footprint box and mark every covered tile occupied.
  // This drain is the single writer of the occupancy slice.
  const box = buildingBoxAt(state.grid, cmd.tile)
  state.buildings.push(makeBuilding(cmd.id, accessSlotCount(def), box))
  for (const t of coveredTiles(state.grid, box)) {
    state.grid.occupied[tileKey(t.tx, t.ty)] = true
  }
}

// Set a chain's target headcount from an assign_labor command. Validates before
// mutating and rejects an invalid command as a no-op (like place_building): a
// bad chain index or a non-finite/negative/fractional count is player/UI error,
// not an authoring bug that must kill the sim. The arbiter reconciles the new
// target against the shared pool on the same tick's assignment phase.
function applyAssignLabor(state: SimState, cmd: AssignLaborCommand): void {
  if (!Number.isInteger(cmd.chain)) return
  if (cmd.chain < 0 || cmd.chain >= state.chains.length) return
  if (!Number.isInteger(cmd.count) || cmd.count < 0) return
  state.chains[cmd.chain].target = cmd.count
}

function applyCommand(state: SimState, content: Content, cmd: Command): void {
  switch (cmd.type) {
    case 'noop':
      return
    case 'place_building':
      applyPlaceBuilding(state, content, cmd)
      return
    case 'spawn_colonist': {
      if (state.units.some((existing) => existing.id === cmd.unitId)) {
        throw new Error(`spawn_colonist: duplicate unit id ${cmd.unitId}`)
      }
      // Jitter each colonist's speed within a band around the command's base
      // speed so the crowd does not move in lockstep. Drawn from the seeded
      // traits sub-stream so the same seed + command log reproduces the same
      // speeds — never Math.random.
      const speed =
        cmd.speed *
        rangeRng(state.rng.streams.traits, SPEED_JITTER_MIN, SPEED_JITTER_MAX)
      const unit: SimUnit = {
        id: cmd.unitId,
        pos: { x: cmd.pos.x, y: cmd.pos.y },
        target: null,
        radius: cmd.radius,
        speed,
        phase: 'idle',
        fromIdx: -1,
        toIdx: -1,
        taskItem: null,
        cargo: null,
        // Unassigned on spawn: the colonist joins the one shared pool at the
        // rally point and stays there until the arbiter draws it to a chain.
        chainId: -1,
      }
      state.units.push(unit)
      state.laborPool.colonistIds.push(cmd.unitId)
      return
    }
    case 'assign_labor':
      applyAssignLabor(state, cmd)
      return
    default: {
      // A new command variant must be handled above.
      const _never: never = cmd
      throw new Error(`unhandled command ${JSON.stringify(_never)}`)
    }
  }
}

// Per-colonist speed jitter band (fraction of the command's base speed), so the
// crowd does not move in lockstep. Drawn from a seeded stream at spawn.
const SPEED_JITTER_MIN = 0.75
const SPEED_JITTER_MAX = 1.25

// Production timing/quantities live in recipe content data (RecipeDef). A recipe
// may omit them; these are the fallbacks applied per field when it does. The
// bundled rows set their own values, so these only matter for a recipe authored
// without explicit tuning.
const DEFAULT_PRODUCTION_TICKS = 1
const DEFAULT_EXTRACT_BUFFER = 1
const DEFAULT_QUANTITY = 1

// The continuous (never-satisfied) sink means `legs` would otherwise grow without
// bound over a long watch — and it is copied by canonicalStringify. Cap it as a
// rolling window: the head is completion telemetry only (nothing renders it), and
// the cap is far above any test's leg count so the first-leg assertions are never
// truncated in tests.
const MAX_LEGS = 256

interface HaulTask {
  fromIdx: number
  toIdx: number
  item: ContentId
}

// Return a hauler to the idle pool: drop its task so the movement phase pools it
// at the rally point instead of leaving it parked at a building it cannot use.
// Reassignment only re-dispatches when a source actually holds stock, so
// genuinely-surplus labor stays pooled and legible rather than clustering at the
// mine/smelter waiting on production.
function returnToIdle(u: SimUnit): void {
  u.phase = 'idle'
  u.fromIdx = -1
  u.toIdx = -1
  u.taskItem = null
  u.target = null
}

/** Key a pickup source + item so haulers en route to it can be counted. */
function sourceKey(fromIdx: number, item: ContentId): string {
  return `${fromIdx}:${item}`
}

// The shared-Labor-Pool arbiter. Reconciles each chain's actual assigned unit
// count to its target, drawing from / returning to the ONE shared pool
// deterministically. Runs before haul dispatch each tick so a just-assigned
// colonist can pick up work the same tick.
//
// Determinism + the oversubscription priority rule: chains are reconciled in
// fixed index order; an under-target chain draws the LOWEST-id colonist in the
// pool; an over-target chain releases its HIGHEST-id idle colonists back to the
// pool. Draw never exceeds pool supply, so the sum of assigned colonists never
// exceeds the colonist count — genuine surplus stays unassigned (chainId -1) and
// pools legibly at the rally point, keeping wasted labor visible.
//
// A colonist is released only while idle (between hauls), so a reassignment
// never strands cargo mid-transit; a chain shedding a busy colonist sheds it
// once its current leg completes, which is why reallocation takes effect within
// a bounded number of ticks rather than instantaneously.
function reconcileLabor(state: SimState): void {
  const chains = state.chains
  if (chains.length === 0) return
  const pool = state.laborPool.colonistIds

  const counts = new Array<number>(chains.length).fill(0)
  for (const u of state.units) {
    if (u.chainId >= 0 && u.chainId < chains.length) counts[u.chainId] += 1
  }

  for (let i = 0; i < chains.length; i++) {
    const target = chains[i].target
    if (counts[i] < target) {
      // Draw lowest-id colonists from the one shared pool until at target or the
      // pool is dry (never over-draw beyond supply).
      let need = target - counts[i]
      while (need > 0 && pool.length > 0) {
        const id = takeLowestId(pool)
        const u = state.units.find((x) => x.id === id)
        // A pool id with no matching unit is stale (only reachable once save/load
        // can seed the pool independently of spawn); drop it and keep drawing
        // instead of consuming a draw slot, so `pooled + assigned === total`
        // holds and a real under-target chain still fills.
        if (u !== undefined) {
          u.chainId = i
          counts[i] += 1
          need -= 1
        }
      }
    } else if (counts[i] > target) {
      // Release highest-id IDLE surplus back to the pool. A busy colonist is left
      // to finish its leg and is released on a later tick once idle.
      releaseHighestIdle(state, i, counts[i] - target)
    }
  }
}

// Remove and return the smallest id in the pool. Deterministic (a fixed rule),
// so replaying the same command log reconciles the same colonists to the same
// chains. Caller guarantees the pool is non-empty.
function takeLowestId(pool: number[]): number {
  let minPos = 0
  for (let i = 1; i < pool.length; i++) {
    if (pool[i] < pool[minPos]) minPos = i
  }
  const id = pool[minPos]
  pool.splice(minPos, 1)
  return id
}

// Release up to `count` of chain `chainIdx`'s idle colonists (highest id first)
// back to the shared pool, clearing their chain affinity.
function releaseHighestIdle(
  state: SimState,
  chainIdx: number,
  count: number,
): void {
  // Release only cargo-free idle units. Idle implies no cargo for every current
  // returnToIdle caller (they run pre-pickup), but gating on cargo here keeps a
  // future caller from pooling a colonist that still holds an item — which would
  // orphan that item and break the pooled/assigned conservation invariant.
  const idle: SimUnit[] = []
  for (const u of state.units) {
    if (u.chainId === chainIdx && u.phase === 'idle' && u.cargo === null) {
      idle.push(u)
    }
  }
  idle.sort((a, b) => b.id - a.id)
  for (let i = 0; i < count && i < idle.length; i++) {
    const u = idle[i]
    u.chainId = -1
    state.laborPool.colonistIds.push(u.id)
  }
}

// Phase 2: reconcile labor to per-chain targets (the shared-pool arbiter), then
// assign each assigned idle colonist its next haul WITHIN its own chain. The
// pull edges are derived once per tick (not per idle unit). A source is only
// offered as many haulers as it currently holds stock for — `inbound` counts
// haulers already en route to each source and never over-dispatches — so exactly
// the labor the chain can use is committed and the genuine surplus stays idle to
// pool visibly rather than piling into an access-slot queue that never clears. An
// unassigned colonist (chainId -1) is offered nothing and stays pooled.
function assignment(state: SimState, content: Content): void {
  reconcileLabor(state)
  if (state.buildings.length === 0) return
  const edges = deriveChainPullEdges(content, state.chain)
  // Count haulers already committed to each pickup source this tick (units
  // heading to a source to collect its item), so we cap dispatch at real stock.
  const inbound = new Map<string, number>()
  for (const u of state.units) {
    if (u.phase === 'to_source' && u.taskItem !== null) {
      const k = sourceKey(u.fromIdx, u.taskItem)
      inbound.set(k, (inbound.get(k) ?? 0) + 1)
    }
  }
  for (const u of state.units) {
    if (u.phase !== 'idle') continue
    // An unassigned colonist serves no chain — it stays pooled.
    if (u.chainId < 0) continue
    const task = deriveTask(state, edges, inbound, u.chainId)
    if (task === null) continue
    const k = sourceKey(task.fromIdx, task.item)
    inbound.set(k, (inbound.get(k) ?? 0) + 1)
    u.fromIdx = task.fromIdx
    u.toIdx = task.toIdx
    u.taskItem = task.item
    u.phase = 'to_source'
    u.target = null
  }
}

/**
 * Next available haul WITHIN the colonist's own chain, or null when nothing is
 * on offer. It scans that chain's pull edges from the downstream (sink) end and
 * returns the first whose source holds MORE of the item than the haulers already
 * inbound to it — so the chain never dispatches more carriers than the source has
 * stock for, and surplus labor is left to pool. Edge indices are chain-relative
 * (0..chainLen-1) and mapped through the chain's `buildingIdx` to flat indices in
 * `state.buildings`, so demand cascades upstream from recipe data alone, scoped
 * to the one chain — labor no longer floats between groups.
 */
function deriveTask(
  state: SimState,
  edges: ReturnType<typeof deriveChainPullEdges>,
  inbound: Map<string, number>,
  chainId: number,
): HaulTask | null {
  const chain = state.chains[chainId]
  if (chain === undefined) return null
  const buildingIdx = chain.buildingIdx

  // The sink is never "satisfied": it keeps pulling so the chain flows
  // continuously and storage accumulates without bound.
  for (let e = edges.length - 1; e >= 0; e--) {
    const edge = edges[e]
    const fromIdx = buildingIdx[edge.fromIdx]
    const toIdx = buildingIdx[edge.toIdx]
    const source = state.buildings[fromIdx]
    if (source === undefined) continue
    const committed = inbound.get(sourceKey(fromIdx, edge.item)) ?? 0
    if ((source.inventory[edge.item] ?? 0) > committed) {
      return { fromIdx, toIdx, item: edge.item }
    }
  }
  return null
}

// Phase 3: pull / demand. No-op stub — demand is resolved in assignment via
// deriveChainPullEdges, whose edges are recipe-as-demand (an edge exists only
// where a downstream recipe consumes the upstream's output). The full
// multi-unit shared-labor-pool arbiter is not built yet.
function pullDemand(_state: SimState, _content: Content): void {}

// Phase 4: move colonists and reserve pickups/dropoffs. Idle colonists pool at
// the rally point; working colonists steer to their target building's access
// slot, or queue past it when the slots are full.
function movement(state: SimState): void {
  const obstacles = state.buildings.map((b) => b.box)
  // Overflow-queue rank counter per building, so waiting colonists line up in a
  // legible row. Deterministic because units iterate in a fixed order.
  const waitCount = new Array<number>(state.buildings.length).fill(0)

  for (const u of state.units) {
    if (u.phase === 'idle' || u.taskItem === null) {
      // Idle labor pools at the rally point so wasted capacity is legible.
      u.target = poolSpot(state.poolPoint, u.id)
      stepUnit(u, state.dt, obstacles)
      continue
    }

    if (u.phase === 'to_source') {
      reserveAtBuilding(
        state,
        u,
        u.fromIdx,
        obstacles,
        'pickup',
        u.taskItem,
        waitCount,
      )
    } else if (u.phase === 'to_dest' && u.cargo !== null) {
      reserveAtBuilding(
        state,
        u,
        u.toIdx,
        obstacles,
        'dropoff',
        u.cargo,
        waitCount,
      )
    }
  }
}

function reserveAtBuilding(
  state: SimState,
  u: SimUnit,
  buildingIdx: number,
  obstacles: Aabb[],
  kind: 'pickup' | 'dropoff',
  item: ContentId,
  waitCount: number[],
): void {
  const building = state.buildings[buildingIdx]
  const slot = claimSlot(building, u.id)
  if (slot === -1) {
    // Slots full: wait in a legible line past the slots rather than stacking
    // on the building. Fresh Vec2 so the unit never aliases sim geometry.
    u.target = queueWaitPos(building, waitCount[buildingIdx]++)
    stepUnit(u, state.dt, obstacles)
    return
  }
  // Copy the slot position: the unit must not alias the building's own Vec2, or
  // an in-place target mutation would move the slot (and a save round-trip would
  // silently split the shared reference).
  u.target = { ...building.slots[slot].pos }
  const arrived = stepUnit(u, state.dt, obstacles)
  if (!arrived) return
  if (kind === 'pickup' && (building.inventory[item] ?? 0) < 1) {
    // Source emptied before this hauler could pick up (over-dispatch or timed
    // production): release the claimed slot and return the hauler to the idle
    // pool so surplus labor pools visibly instead of parking on an empty source
    // and blocking other haulers.
    releaseSlot(building, u.id)
    returnToIdle(u)
    return
  }
  state.pendingTransfers.push({ kind, unitId: u.id, buildingIdx, item })
}

// Phase 5: apply reserved transfers to inventories.
function transferCommit(state: SimState): void {
  for (const t of state.pendingTransfers) {
    const u = state.units.find((x) => x.id === t.unitId)
    const b = state.buildings[t.buildingIdx]
    if (u === undefined || b === undefined) continue

    if (t.kind === 'pickup') {
      const have = b.inventory[t.item] ?? 0
      if (have < 1) {
        // Lost the race for the last unit of stock this tick: release the slot
        // and return to idle so this hauler pools rather than parking on the
        // now-empty source.
        releaseSlot(b, u.id)
        returnToIdle(u)
        continue
      }
      b.inventory[t.item] = have - 1
      u.cargo = t.item
      releaseSlot(b, u.id)
      u.phase = 'to_dest'
      u.target = null
    } else {
      b.inventory[t.item] = (b.inventory[t.item] ?? 0) + 1
      state.legs.push({
        from: state.buildings[u.fromIdx].id,
        to: b.id,
        item: t.item,
      })
      if (state.legs.length > MAX_LEGS) state.legs.shift()
      releaseSlot(b, u.id)
      u.cargo = null
      u.taskItem = null
      u.fromIdx = -1
      u.toIdx = -1
      u.phase = 'idle'
      u.target = null
    }
  }
  state.pendingTransfers = []
}

/**
 * True when the extractor at `extractorIdx` holds an extraction slot on some
 * adjacent patch yielding its output. Slots are claimed deterministically in
 * flat building order: for a given patch, the first N fromPatch extractors (in
 * index order) adjacent to it and matching its item take its N slots; an
 * extractor whose rank on every adjacent patch is at or past the cap gets no
 * slot and idles. Recomputed each tick from static geometry (no persistent
 * slot-occupancy field), which keeps production the single writer of the
 * extractor's own inventory and sidesteps a stored-occupancy writer.
 *
 * v1 seeds exactly one patch per extractor, so an extractor is adjacent to at
 * most one matching patch and the per-patch rank is an exact slot assignment.
 * When several patches are adjacent to one extractor the greedy per-patch count
 * can over-restrict (the extractor is counted against every patch it touches).
 * TODO(story 8-2): replace with a true lowest-index-per-patch assignment once
 * multi-patch contention arrives.
 */
function extractorHasPatchSlot(
  state: SimState,
  content: Content,
  extractorIdx: number,
  output: ContentId,
): boolean {
  const ext = state.buildings[extractorIdx]
  for (const patch of state.patches) {
    if (patch.item !== output) continue
    // Build the patch's footprint tile-set once, then test the extractor and each
    // earlier extractor against it — instead of rebuilding it per adjacency call.
    const patchTiles = tileKeySet(state.grid, patch.box)
    if (!boxAdjacentToTileSet(state.grid, ext.box, patchTiles)) continue
    let rank = 0
    for (let j = 0; j < extractorIdx; j++) {
      const other = state.buildings[j]
      const otherRecipe = recipeOf(content, other.id)
      if (
        otherRecipe?.fromPatch &&
        otherRecipe.output === output &&
        boxAdjacentToTileSet(state.grid, other.box, patchTiles)
      ) {
        rank += 1
      }
    }
    if (rank < patch.extractionSlots) return true
  }
  return false
}

// Phase 6: production. Producing takes time: an extractor and a converter each
// run a cooldown (recipe.ticks) before their output is ready, so buildings do
// not restock instantly and haulers visibly wait on production. Timing, buffer
// and per-batch quantities all come from the recipe data — this branches on
// recipe SHAPE, never on a specific building/recipe/item id. loadContent rejects
// any output-only non-patch shape, so only the three valid shapes reach here.
function production(state: SimState, content: Content): void {
  for (let idx = 0; idx < state.buildings.length; idx++) {
    const b = state.buildings[idx]
    const recipe = recipeOf(content, b.id)
    if (recipe === undefined) continue

    const ticks = recipe.ticks ?? DEFAULT_PRODUCTION_TICKS
    const outputQty = recipe.outputQty ?? DEFAULT_QUANTITY

    if (recipe.fromPatch && recipe.output !== undefined) {
      // The extractor is the chain head only when bound to an adjacent patch
      // with a free extraction slot. Unbound (no adjacent matching patch, or all
      // adjacent patches' slots taken) it idles — no output, no cooldown spend —
      // the same observable "not producing" state a starved converter leaves.
      if (!extractorHasPatchSlot(state, content, idx, recipe.output)) continue
      // Infinite patch: accumulate output up to bufferCap, spending recipe.ticks
      // on each batch, so several haulers can work the chain head at once while
      // the buffer is stocked.
      const bufferCap = recipe.bufferCap ?? DEFAULT_EXTRACT_BUFFER
      if ((b.inventory[recipe.output] ?? 0) >= bufferCap) continue
      if (b.cooldown > 0) {
        b.cooldown -= 1
        if (b.cooldown === 0) {
          b.inventory[recipe.output] =
            (b.inventory[recipe.output] ?? 0) + outputQty
        }
      } else {
        b.cooldown = ticks
      }
    } else if (recipe.input !== undefined && recipe.output !== undefined) {
      // Converter: consume the recipe's input quantity to start a batch, then
      // spend recipe.ticks before the output appears. A starved building (input
      // below the batch quantity, no batch in progress) does nothing — no
      // consume, no emit, no cooldown spend — leaving an observable idle state.
      const inputQty = recipe.inputQty ?? DEFAULT_QUANTITY
      if (b.cooldown > 0) {
        b.cooldown -= 1
        if (b.cooldown === 0) {
          b.inventory[recipe.output] =
            (b.inventory[recipe.output] ?? 0) + outputQty
        }
      } else if ((b.inventory[recipe.input] ?? 0) >= inputQty) {
        b.inventory[recipe.input] -= inputQty
        b.cooldown = ticks
      }
    }
    // Pure sink: holds delivered items and accumulates; no consumption.
  }
}

// Phase 7: population / food. No-op stub.
function population(_state: SimState): void {}

// Phase 8: contracts / events. Draws one value from the events stream each tick
// so its cursor advances; real contracts/events land later.
function events(state: SimState): void {
  state.lastEventRoll = nextRng(state.rng.streams.events)
}

// Phase 9: bookkeeping.
function bookkeeping(state: SimState): void {
  state.tick += 1
}

/** Run one fixed-step tick through the nine phases. */
export function tick(state: SimState, content: Content): void {
  drainCommands(state, content) // 1
  assignment(state, content) // 2
  pullDemand(state, content) // 3
  movement(state) // 4  reserve
  transferCommit(state) // 5  commit
  production(state, content) // 6
  population(state) // 7
  events(state) // 8
  bookkeeping(state) // 9
}

/**
 * Accumulate real time and run whole fixed-step ticks. Runs one tick per whole
 * `dt` accumulated, never a larger step; a sub-dt delta runs nothing and a zero
 * delta pauses. Returns the number of ticks run.
 */
export function advance(
  state: SimState,
  content: Content,
  realDeltaSeconds: number,
): number {
  if (!Number.isFinite(realDeltaSeconds) || realDeltaSeconds < 0) {
    throw new Error('advance: realDeltaSeconds must be a finite number >= 0')
  }
  state.accumulator += realDeltaSeconds
  let steps = 0
  while (state.accumulator >= state.dt) {
    tick(state, content)
    state.accumulator -= state.dt
    steps += 1
  }
  return steps
}
